import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getFactsList } from '../../../services';

const FactsContext = createContext();

const isBodyEmpty = (body) => {
  if (body === null || body === undefined) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

const initialState = {
  baseFacts: [],
  baseMeta: null,
  baseKnowledgebaseUuid: null,
  facts: [],
  factsMeta: null,
  loading: false,
  error: null,
  isEmpty: false,
};

export const FactsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchFacts = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getFactsList(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const body = Array.isArray(response.body) ? response.body : [];
        const isEmpty = isBodyEmpty(body);
        const knowledgebaseUuid = params?.knowledgebase_uuid ?? null;

        if (isEmpty) {
          setState((prev) => ({
            ...prev,
            baseFacts: persistAsBase ? [] : prev.baseFacts,
            baseMeta: persistAsBase ? null : prev.baseMeta,
            baseKnowledgebaseUuid: persistAsBase ? null : prev.baseKnowledgebaseUuid,
            facts: [],
            factsMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: true,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            baseFacts: persistAsBase ? body : prev.baseFacts,
            baseMeta: persistAsBase ? (response.meta || null) : prev.baseMeta,
            baseKnowledgebaseUuid: persistAsBase ? knowledgebaseUuid : prev.baseKnowledgebaseUuid,
            facts: body,
            factsMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: false,
          }));
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error('Error fetching facts:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          facts: [],
          factsMeta: null,
          isEmpty: false,
          ...(persistAsBase
            ? {
                baseFacts: [],
                baseMeta: null,
                baseKnowledgebaseUuid: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const resetFactsToBase = useCallback(() => {
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;

    setState((prev) => ({
      ...prev,
      facts: prev.baseFacts,
      factsMeta: prev.baseMeta,
      loading: false,
    }));
  }, []);

  const resetFacts = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    baseFacts: state.baseFacts,
    baseMeta: state.baseMeta,
    baseKnowledgebaseUuid: state.baseKnowledgebaseUuid,
    facts: state.facts,
    factsMeta: state.factsMeta,
    loading: state.loading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchFacts,
    resetFactsToBase,
    resetFacts,
  };

  return (
    <FactsContext.Provider value={value}>
      {children}
    </FactsContext.Provider>
  );
};

export const useFactsContext = () => {
  const context = useContext(FactsContext);
  if (!context) {
    throw new Error('useFactsContext must be used within FactsProvider');
  }
  return context;
};
